import { basePostHogClientOptions } from '@bazak/analytics/client';
import posthog from 'posthog-js';

import { DEPLOY_ENV, IS_TRACKING_ENABLED, POSTHOG_HOST } from '@/common/constants';
import { isInIframe } from '@/lib/utils/dom';
import {
  type BootstrapCookieValue,
  hasStoredPostHogIdentity,
  readBootstrapCookie,
} from '@/lib/utils/posthog-bootstrap-client';
import { clearUrlParam, getUrlParam } from '@/lib/utils/url-params';

interface PostHogBootstrap extends BootstrapCookieValue {
  isIdentifiedID?: boolean;
}

// Never load analytics on verification pages. Embedded forms also require ph-no-capture.
const isAnalyticsFreePath = /^\/auth\/[^/]+\/verify\/*$/.test(window.location.pathname);

if (IS_TRACKING_ENABLED && process.env.NEXT_PUBLIC_POSTHOG_KEY && !isAnalyticsFreePath) {
  const inIframe = isInIframe();

  // The parent widget injects its first-party distinct_id via `?parentDistinctId=…` (see
  // `widget-core.ts`). It traces back to the parent's own localStorage, so it outranks the
  // bootstrap cookie, which is third-party on bazak.ai and unreliable on mobile.
  const parentDistinctId = inIframe ? getUrlParam('parentDistinctId') : null;
  if (parentDistinctId) {
    // Keep it out of Referer headers, document.referrer snapshots, and error-report URLs.
    clearUrlParam('parentDistinctId');
  }

  // Precedence: the parent's id, else whatever posthog already persisted, else the bootstrap
  // cookie. That cookie seeds a cold client only — it carries an anonymous server-minted id, and
  // posthog's init writes an anonymous bootstrap straight over a stored identity, re-basing the
  // visitor mid-conversation so the server refuses their next message on a thread they still see.
  //
  // `isIdentifiedID: true` is critical on the parent id: it makes posthog merge any pre-existing
  // anonymous identity (e.g. a prior session whose localStorage survived ITP) into it via
  // $identify. Without the flag posthog's `_init` takes the ELSE branch and orphans that session.
  //
  // The cookie's sessionID is dropped with it: paired with a different distinct_id it risks
  // stitching one user's session under another's identity on a shared device.
  let bootstrap: PostHogBootstrap | undefined;
  if (parentDistinctId) bootstrap = { distinctID: parentDistinctId, isIdentifiedID: true };
  else if (!hasStoredPostHogIdentity()) bootstrap = readBootstrapCookie() ?? undefined;

  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    ...basePostHogClientOptions({ host: POSTHOG_HOST }),
    // The shared defaults switch flags off — they cost a request per load and most apps never read
    // one. This app does: the rooms experiment is assigned in the browser, on the id that carries the
    // guest's order. With flags off, `getFeatureFlag` answers `undefined` forever, which reads as
    // "no experiment" and quietly hands every guest the control experience.
    advanced_disable_feature_flags: false,
    advanced_disable_feature_flags_on_first_load: false,
    // In-iframe (widget): the host page owns recording/pageviews/autocapture.
    disable_session_recording: inIframe,
    capture_pageview: !inIframe,
    capture_pageleave: !inIframe,
    autocapture: !inIframe,
    before_send: event => {
      if (event?.event === '$exception') {
        const exceptions = event.properties?.$exception_list;
        if (
          Array.isArray(exceptions) &&
          exceptions.some((e: { value?: string }) => e.value && /ResizeObserver loop/.test(e.value))
        ) {
          return null;
        }
      }
      return event;
    },
    ...(bootstrap && { bootstrap }),
  });

  // Stamp `environment` as a super-property so EVERY event (autocapture,
  // pageview, manual `posthog.capture`) carries it from the first event.
  // `WidgetAnalyticsProvider` also calls `register({ environment })` later
  // with tenantKey/widgetVersion, but that runs inside `useLayoutEffect` and
  // would miss the initial pageview without this earlier call.
  posthog.register({ environment: DEPLOY_ENV });
}
